import * as THREE from 'three';
import { materialLibrary as M } from '../MaterialLibrary';
import { SIGNAL } from '../MaterialLibrary';

// World prop kit: islands (rock + sand + palms), buoys, crates, docks,
// lighthouse, distant islands. All procedural, instanced where repeated.

export function createPalm(scale = 1): THREE.Group {
  const g = new THREE.Group();
  const trunkSegments = 5;
  let y = 0;
  let bend = 0;
  const trunkGeo = new THREE.CylinderGeometry(0.09, 0.14, 0.75, 6);
  for (let i = 0; i < trunkSegments; i++) {
    const seg = new THREE.Mesh(trunkGeo, M.palmTrunk);
    seg.position.set(bend, y + 0.36, 0);
    seg.rotation.z = -bend * 0.5;
    g.add(seg);
    y += 0.7;
    bend += 0.08;
  }
  const crown = new THREE.Group();
  crown.position.set(bend, y + 0.1, 0);
  const leafGeo = new THREE.BufferGeometry();
  // Curved leaf blade: quad strip
  const pts: number[] = [];
  const idx: number[] = [];
  const leafLen = 1.5;
  const bladeW = 0.16;
  for (let i = 0; i <= 6; i++) {
    const t = i / 6;
    const x = t * leafLen;
    const sag = -Math.pow(t, 2) * 0.7;
    const w = bladeW * (1 - t * 0.85);
    pts.push(x, sag + 0.02, -w);
    pts.push(x, sag - 0.02, w);
  }
  for (let i = 0; i < 6; i++) {
    idx.push(i * 2, i * 2 + 1, i * 2 + 3);
    idx.push(i * 2, i * 2 + 3, i * 2 + 2);
  }
  leafGeo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  leafGeo.setIndex(idx);
  leafGeo.computeVertexNormals();
  for (let i = 0; i < 7; i++) {
    const leaf = new THREE.Mesh(leafGeo, M.palmLeaf);
    leaf.rotation.y = (i / 7) * Math.PI * 2 + Math.sin(i * 3) * 0.2;
    leaf.rotation.z = -0.15 - (i % 3) * 0.12;
    crown.add(leaf);
  }
  // Coconuts
  const cocoGeo = new THREE.SphereGeometry(0.09, 6, 5);
  for (let i = 0; i < 3; i++) {
    const coco = new THREE.Mesh(cocoGeo, M.wood);
    coco.position.set(Math.cos(i * 2.1) * 0.16, -0.08, Math.sin(i * 2.1) * 0.16);
    crown.add(coco);
  }
  g.add(crown);
  g.scale.setScalar(scale);
  return g;
}

export function createIsland(radius: number, palms = 4, seed = 0): THREE.Group {
  const g = new THREE.Group();
  const rng = mulberry(seed);

  // Rock base: displaced icosphere
  const rockGeo = new THREE.IcosahedronGeometry(radius, 2);
  const pos = rockGeo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const n = Math.sin(x * 3.1 + seed) * Math.cos(z * 2.7 - seed) * 0.18
      + Math.sin(y * 5.3 + x) * 0.1;
    const s = 1 + n;
    pos.setXYZ(i, x * s, Math.max(0, y * s * 1.6), z * s);
  }
  rockGeo.computeVertexNormals();
  const rock = new THREE.Mesh(rockGeo, M.rock);
  rock.position.y = radius * 0.3;
  rock.castShadow = true;
  rock.receiveShadow = true;
  g.add(rock);

  // Sand ring
  const sand = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 1.45, radius * 1.2, radius * 0.32, 14),
    M.sand,
  );
  sand.position.y = -radius * 0.12;
  sand.receiveShadow = true;
  g.add(sand);

  for (let i = 0; i < palms; i++) {
    const palm = createPalm(0.8 + rng() * 0.55);
    const a = rng() * Math.PI * 2;
    const r = radius * (0.25 + rng() * 0.4);
    palm.position.set(Math.cos(a) * r, radius * 0.4, Math.sin(a) * r);
    palm.rotation.y = rng() * Math.PI * 2;
    palm.traverse((o) => {
      if (o instanceof THREE.Mesh) o.castShadow = true;
    });
    g.add(palm);
  }
  return g;
}

export function createBuoy(): THREE.Group {
  const g = new THREE.Group();
  const float = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.42, 0.55, 10), M.hazard);
  float.position.y = 0.28;
  float.castShadow = true;
  g.add(float);
  const band = new THREE.Mesh(new THREE.CylinderGeometry(0.33, 0.33, 0.12, 10), M.whitePaint);
  band.position.y = 0.4;
  g.add(band);
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.9, 6), M.trim);
  mast.position.y = 0.95;
  g.add(mast);
  const beacon = new THREE.Mesh(
    new THREE.SphereGeometry(0.09, 8, 8),
    new THREE.MeshStandardMaterial({ color: '#200604', emissive: SIGNAL.danger, emissiveIntensity: 2.5, roughness: 0.4 }),
  );
  beacon.position.y = 1.42;
  g.add(beacon);
  g.userData.beacon = beacon;
  return g;
}

export function createCrateStack(seed = 1): THREE.Group {
  const rng = mulberry(seed);
  const g = new THREE.Group();
  const crateGeo = new THREE.BoxGeometry(1, 1, 1);
  const edgeMat = new THREE.MeshStandardMaterial({ color: '#6b4b2a', roughness: 0.85 });
  const faceMat = new THREE.MeshStandardMaterial({ color: '#c99b58', roughness: 0.8 });
  for (let i = 0; i < 3; i++) {
    const size = 0.9 + rng() * 0.5;
    const crate = new THREE.Mesh(crateGeo, i % 2 ? edgeMat : faceMat);
    crate.scale.setScalar(size);
    crate.position.set((rng() - 0.5) * 1.6, size / 2 + i * 0.02, (rng() - 0.5) * 1.6);
    crate.rotation.y = rng() * 0.8;
    crate.castShadow = true;
    crate.receiveShadow = true;
    g.add(crate);
    // hazard tape band
    const tape = new THREE.Mesh(
      new THREE.TorusGeometry(size * 0.72, 0.03, 6, 4),
      new THREE.MeshBasicMaterial({ color: SIGNAL.hazard }),
    );
    tape.rotation.x = Math.PI / 2;
    tape.rotation.z = Math.PI / 4;
    tape.position.copy(crate.position);
    tape.position.y = size * 0.7;
    g.add(tape);
  }
  return g;
}

export function createDock(): THREE.Group {
  const g = new THREE.Group();
  const plankGeo = new THREE.BoxGeometry(0.4, 0.1, 6);
  for (let i = 0; i < 8; i++) {
    const plank = new THREE.Mesh(plankGeo, M.wood);
    plank.position.set((i - 3.5) * 0.44, 0.55, 0);
    plank.castShadow = true;
    plank.receiveShadow = true;
    g.add(plank);
  }
  const postGeo = new THREE.CylinderGeometry(0.09, 0.11, 1.4, 7);
  for (const [x, z] of [[-1.6, -2.6], [1.6, -2.6], [-1.6, 2.6], [1.6, 2.6]]) {
    const post = new THREE.Mesh(postGeo, M.palmTrunk);
    post.position.set(x, -0.05, z);
    post.castShadow = true;
    g.add(post);
  }
  return g;
}

export function createLighthouse(): THREE.Group {
  const g = new THREE.Group();
  const towerGeo = new THREE.CylinderGeometry(0.9, 1.4, 7, 12);
  const tower = new THREE.Mesh(towerGeo, M.whitePaint);
  tower.position.y = 3.5;
  tower.castShadow = true;
  g.add(tower);
  // red bands
  const bandGeo = new THREE.CylinderGeometry(1.12, 1.2, 0.8, 12);
  for (let i = 0; i < 3; i++) {
    const band = new THREE.Mesh(bandGeo, M.hullRed);
    band.scale.setScalar(1 - i * 0.18);
    band.position.y = 1.2 + i * 2.2;
    g.add(band);
  }
  const lampRoom = new THREE.Mesh(new THREE.CylinderGeometry(0.75, 0.85, 0.7, 10), M.trim);
  lampRoom.position.y = 7.35;
  g.add(lampRoom);
  const lamp = new THREE.Mesh(
    new THREE.SphereGeometry(0.42, 12, 10),
    new THREE.MeshStandardMaterial({ color: '#221a05', emissive: '#ffdf8a', emissiveIntensity: 3.2, roughness: 0.3 }),
  );
  lamp.position.y = 7.7;
  g.add(lamp);
  g.userData.lamp = lamp;
  const cap = new THREE.Mesh(new THREE.ConeGeometry(0.95, 0.7, 10), M.hullRed);
  cap.position.y = 8.4;
  g.add(cap);
  return g;
}

export function createDistantIsland(radius: number, color: THREE.Color): THREE.Group {
  // Cheap far silhouette: low-poly cone mesh with flat shading tint
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    color, roughness: 1.0, metalness: 0.0, flatShading: true,
  });
  const geo = new THREE.ConeGeometry(radius, radius * 0.55, 7, 2);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.y = -radius * 0.08;
  g.add(mesh);
  return g;
}

function mulberry(seed: number): () => number {
  let t = seed + 0x6d2b79f5;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
